import type {FormField, FormSection, StepDetail} from '../dtos';
import type {PaymentUpdateTerms} from '../dtos/payment-plan-update';
import {computeLiveGates, type LiveGates} from './conditions';
import {fieldRuleErrors} from './validate';

const fieldKey = (section: FormSection, field: FormField): string => field.k ?? field.ref ?? `${section.name}.${field.l}`;

export function draftGates(step: StepDetail, values: Record<string, unknown>): LiveGates {
 const server = {hiddenFieldKeys: step.hiddenFieldKeys ?? [], lockedFieldKeys: step.lockedFieldKeys ?? [], hiddenSectionNames: step.hiddenSectionNames ?? [], hiddenOptionValues: step.hiddenOptionValues ?? {}};
 // Missing rule facts means the server verdict is authoritative. An absent precondition is
 // itself supported/visible in computeLiveGates, so stripping rules would erase these gates.
 if (step.preconditionFacts === undefined) {
  const hiddenSections = new Set(server.hiddenSectionNames);
  const sectionFieldKeys = (step.formSchema ?? []).filter(section => hiddenSections.has(section.name))
   .flatMap(section => section.fields.map(field => fieldKey(section, field)));
  return {
   hiddenFields: new Set([...server.hiddenFieldKeys, ...sectionFieldKeys]),
   lockedFields: new Set(server.lockedFieldKeys),
   hiddenSections,
   hiddenOptions: new Map(Object.entries(server.hiddenOptionValues).map(([key, options]) => [key, new Set(options)])),
  };
 }
 return computeLiveGates(step.formSchema ?? [], values, server);
}
function fieldWritable(step: StepDetail, key: string, values: Record<string, unknown>): boolean {
 const gates = draftGates(step, values);
 return !gates.hiddenFields.has(key) && !gates.lockedFields.has(key) && (step.formSchema ?? []).some(section => section.fields.some(field => fieldKey(section, field) === key && !field.ref && !field.readonly && !(field.derive && !field.deriveEditable)));
}

/** Keys the step owns an answer for — everything the revision read model, not an ambient fact, fills. */
function ownAnswerKeys(step: StepDetail, mapped: string[]): string[] {
 return (step.formSchema ?? [])
  .flatMap(section => section.fields
   .filter(field => !field.ref && !field.readonly && !(field.derive && !field.deriveEditable))
   .map(field => fieldKey(section, field)))
  .filter(key => !mapped.includes(key));
}

/** Payment rules address both bare and qualified keys. Match the server's proposed-term overlay. */
function mirrorQualifiedTerms(values: Record<string, unknown>, mapped: string[]): void {
 for (const key of Object.keys(values)) {
  const source = /^@s\d+:(.+)$/.exec(key)?.[1];
  if (source && mapped.includes(source)) values[key] = values[source];
 }
}

function stampStepOrder(values: Record<string, unknown>, order: number): void {
 for (const [key, value] of Object.entries(values)) {
  if (!key.startsWith('@s')) values[`@s${order}:${key}`] = value;
 }
}

export function draftValues(step: StepDetail, terms: PaymentUpdateTerms, data: Record<string, unknown>): Record<string, unknown> {
 const mappings = (step.uiFlags?.paymentPlanUpdate as {fieldKeys?: Record<string, string>} | undefined)?.fieldKeys ?? {};
 const mapped = Object.values(mappings);
 const values: Record<string, unknown> = {...step.preconditionFacts, ...step.upstreamValues, ...step.prefill};
 // Own answers come only from the revision read model, never ambient same-key facts.
 for (const key of ownAnswerKeys(step, mapped)) values[key] = undefined;
 Object.assign(values, data);
 for (const [term, key] of Object.entries(mappings)) values[key] = terms[(term === 'specialInstallmentsCount' ? 'leadingMonths' : term) as keyof PaymentUpdateTerms];
 mirrorQualifiedTerms(values, mapped);
 const order = (step as {stepOrder?: number}).stepOrder;
 if (order !== undefined) stampStepOrder(values, order);
 return values;
}

/** PATCH only changed, still-applicable answers. Removal reaches a fixed point so an inactive
 * ancillary controller cannot keep another pending answer eligible. The server remains authoritative. */
export function draftFieldPatch(step: StepDetail, terms: PaymentUpdateTerms, data: Record<string, unknown>, base: Record<string, unknown>, editable: string[], published: Record<string, unknown> = base): Record<string, unknown> {
 let active = {...data};
 for (;;) {
  const values = draftValues(step, terms, {...published, ...active});
  const next = Object.fromEntries(Object.entries(active).filter(([key]) => fieldWritable(step, key, values)));
  if (Object.keys(next).length === Object.keys(active).length) break;
  active = next;
 }
 return Object.fromEntries(Object.entries(active).filter(([key, value]) => editable.includes(key) && JSON.stringify(value) !== JSON.stringify(base[key])));
}

/** Match the server's payment-input gate while allowing incomplete ancillary draft answers. */
export function paymentUpdateValidationErrors(step: StepDetail, terms: PaymentUpdateTerms, data: Record<string, unknown>): string[] {
 const tenors = (step.globals ?? []).find(global => global.widget?.dbrEligibility?.tenors?.length)?.widget?.dbrEligibility?.tenors ?? [];
 const tenorError = tenors.length && !tenors.some(row => row.tenorYears === terms.tenorYears) ? [`Choose a supported tenor: ${tenors.map(row => row.tenorYears).join(' or ')} years.`] : [];

 const mappings = (step.uiFlags?.paymentPlanUpdate as {fieldKeys?: Record<string, string>} | undefined)?.fieldKeys ?? {};
 const keys = new Set(Object.values(mappings));
 const schema = (step.formSchema ?? []).filter(section => section.fields.some(field => keys.has(fieldKey(section, field))));
 return [...new Set([...tenorError, ...Object.values(fieldRuleErrors(schema, draftValues(step, terms, data)))])];
}
